import { SettingsActionRow } from '@account/components';
import { Alert } from '@components/display/alerts';
import { Button } from '@components/utility/buttons';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import { AlertTitle, Chip, CircularProgress, Divider, Skeleton, Typography, useMediaQuery } from '@mui/material';
import { useGetUserCredentialTypesQuery } from '@services/keycloak';
import clsx from 'clsx';
import { FormattedMessage } from 'react-intl';

export const AccountKeycloak = () => {
    const { data: userCredentialTypes, isLoading: isLoadingUserCredentialTypes } = useGetUserCredentialTypesQuery();
    const has2FA = !!userCredentialTypes?.length && userCredentialTypes.some((cred) => cred.type === 'otp');
    const isBelowMd = useMediaQuery((theme) => theme.breakpoints.down('md'));

    return (
        <>
            <Typography py="26px" fontFamily="museo-sans" letterSpacing="-1%" fontSize="26px">
                <FormattedMessage
                    id="accounts.password_and_2fa"
                    defaultMessage="Password and Two Factor Authentication"
                />
            </Typography>
            <Divider />
            <Alert.Info className="my-[24px]">
                <AlertTitle>
                    <FormattedMessage
                        id="accounts.security.settings"
                        defaultMessage="Manage your security settings on Victron OneID"
                    />
                </AlertTitle>
                <FormattedMessage
                    id="accounts.security.settings.description"
                    defaultMessage="To change your password or update your Two-Factor Authentication (2FA), please visit your Victron OneID profile. These settings are managed centrally to keep your account secure."
                />
            </Alert.Info>
            <SettingsActionRow
                icon={<ShieldOutlinedIcon fontSize="large" className="scale-90" />}
                title={<FormattedMessage id="accounts.2fa.title" defaultMessage="Two Factor Authentication" />}
                description={
                    <Typography color="neutral-800" fontSize={13}>
                        {isLoadingUserCredentialTypes ? (
                            <Skeleton />
                        ) : has2FA ? (
                            <FormattedMessage
                                id="accounts.2fa.protected"
                                defaultMessage="Your account is protected with 2FA"
                            />
                        ) : (
                            <FormattedMessage
                                id="accounts.2fa.not_protected"
                                defaultMessage="Your account is not protected with 2FA"
                            />
                        )}
                    </Typography>
                }
                rel="noreferrer"
                trailingAction={
                    isLoadingUserCredentialTypes ? (
                        <CircularProgress size={20} />
                    ) : (
                        <Chip
                            color={has2FA ? 'light-success' : 'light-error'}
                            label={
                                <Typography fontSize={13} fontWeight={600}>
                                    {has2FA ? (
                                        <FormattedMessage id="accounts.2fa.active" defaultMessage="Active" />
                                    ) : (
                                        <FormattedMessage id="accounts.2fa.inactive" defaultMessage="Inactive" />
                                    )}
                                </Typography>
                            }
                            className={clsx({ 'w-full': isBelowMd })}
                        />
                    )
                }
                disabled
            />
            <a
                href={`${import.meta.env.VITE_KEYCLOAK_URL}/account/account-security/signing-in`}
                target="_blank"
                className={clsx('w-fit block ml-auto mt-[24px]', { 'w-full': isBelowMd })}
            >
                <Button fullRounded size="xl" fullWidth>
                    <Typography fontSize={16} fontWeight={500}>
                        <FormattedMessage
                            id="accounts.security.go_to_victron_oneid"
                            defaultMessage="Go to Victron OneID"
                        />
                    </Typography>
                </Button>
            </a>
        </>
    );
};
